import React, { useReducer, useState } from 'react';
import { BackArrow } from '../../../../components/BackArrow';
import { Card } from '../../../../components/Card';
import { NameAndTemplate, StartingPoint } from './NameAndTemplate';
import { CardHeader } from './CardHeader';
import { SectionList } from '../../../survey_engine/SurveyTemplates/SectionList';
import { SectionData } from '../../../survey_engine/SurveyTemplates/Section';
import {
  Action,
  initSections,
  reducer,
} from '../../../survey_engine/SurveyTemplates/dispatcher';
import { ScorecardTemplateService } from '../../../../services/v1/applicant_tracking/ScorecardTemplateService';
import { PageTitle } from '../../../../components/PageTitle';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { randomInt } from '../../../../utils/number';
import {
  FieldConfig,
  FieldData,
} from '../../../survey_engine/SurveyTemplates/Field';

const FIELD_CONFIGS: FieldConfig[] = [
  {
    label: '5 Star Scale Response',
    fieldType: 'rating',
    max: 5,
  },
  {
    label: 'Open Text Response',
    fieldType: 'text',
  },
];

export default function NewScorecardTemplatePage() {
  // State
  const [name, setName] = useState<string>('');
  const [startingPoint, setStartingPoint] = useState<StartingPoint>(null);
  const [sections, dispatch] = useReducer(reducer, null, initSections);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Derived State
  function isSavable() {
    if (!name) return false;
    if (startingPoint === undefined) return false;

    for (const section of sections) {
      if (!section.name) return false;

      for (const field of section.fields) {
        if (!field.fieldType) return false;
        if (!field.title) return false;
      }
    }

    return true;
  }

  // Handlers
  async function handleStartingPointChange(startingPoint: StartingPoint) {
    setStartingPoint(startingPoint);

    if (startingPoint == null) return;

    if (typeof startingPoint === 'string' && startingPoint === 'CUSTOM') {
      dispatch({ type: 'reset', newState: initSections() });
      return;
    }

    setIsLoading(true);

    const newSections = await fetchSelectedTemplate(startingPoint.value);
    dispatch({ type: 'reset', newState: newSections });

    setIsLoading(false);
  }

  async function handleSave() {
    await ScorecardTemplateService.create({
      name,
      sections: sections.map((section) => ({
        name: section.name,
        questions: section.fields.map((field) => ({
          title: field.title,
          fieldType: field.fieldType,
          max: field.max,
        })),
      })),
    });

    window.location.href = '/organization_settings/scorecard_templates';
  }

  if (isLoading) return <LoadingSpinner showBackdrop />;

  return (
    <UI
      isSavable={isSavable()}
      name={name}
      onName={setName}
      startingPoint={startingPoint}
      onStartingPointChange={handleStartingPointChange}
      sections={sections}
      dispatch={dispatch}
      onSave={handleSave}
    />
  );
}

function UI(props: {
  isSavable: boolean;
  name: string;
  onName: (value: string) => void;
  startingPoint: StartingPoint;
  onStartingPointChange: (value: StartingPoint) => void;
  sections: SectionData[];
  dispatch: React.Dispatch<Action>;
  onSave: () => void;
}) {
  return (
    <div className='container'>
      <PageHeader />
      <Card>
        <CardHeader isSavable={props.isSavable} onSaveClick={props.onSave} />
        <NameAndTemplate
          name={props.name}
          onName={props.onName}
          startingPoint={props.startingPoint}
          onStartingPointChange={props.onStartingPointChange}
        />
        <SectionList
          fieldConfigs={FIELD_CONFIGS}
          singleTypeSections={true}
          sections={props.sections}
          dispatch={props.dispatch}
        />
      </Card>
    </div>
  );
}

function PageHeader() {
  return (
    <div className='d-flex align-items-center mb-4'>
      <BackArrow />
      <PageTitle text='Add Scorecard' />
    </div>
  );
}

async function fetchSelectedTemplate(id: string) {
  const template = await ScorecardTemplateService.show(id, {
    expand: [
      'survey_template',
      'survey_template.sections',
      'survey_template.sections.questions',
    ],
  });

  const newState: SectionData[] = [];
  for (const sSection of template.surveyTemplate.sections) {
    const cFields: FieldData[] = [];
    for (const sField of sSection.questions) {
      const config = FIELD_CONFIGS.find(
        (config) => config.fieldType === sField.fieldType,
      );
      cFields.push({
        sId: sField.id,
        cId: randomInt(),
        title: sField.title,
        ...config,
      });
    }

    newState.push({
      sId: sSection.id,
      cId: randomInt(),
      name: sSection.name,
      fields: cFields,
    });
  }

  return newState;
}
